import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientLicenseEntity } from './client-license.entity';

/**
 * Per-license, per-field snooze record for the Discrepancies tab.
 *
 * When a tenant user clicks Snooze on a soft (license_number / name)
 * discrepancy, we capture both sides of the value pair AT THAT MOMENT.
 * That pair is what the snooze is bound to — if either side drifts
 * upstream (the registry value changes, or the stored CRM value gets
 * cleaned up), the snooze becomes inert and the discrepancy re-raises
 * with the new pair so the rep can review it again.
 *
 * Hard alerts (status drift to Revoked/Suspended/Rejected, expiration
 * mismatch) are NOT snoozable — those signals must always be visible.
 * This entity exists only for the soft tier.
 *
 * No time expiry — confirmed 2026-05-16. Re-review is triggered solely
 * by value-pair drift; periodic time-based re-review was decided to be
 * noise rather than signal.
 *
 * Auto-clear: when the discrepancy service evaluates display state and
 * the underlying discrepancy condition no longer exists (e.g., the rep
 * fixed the value in their ERP and it flowed back), the snooze row is
 * left in place but the discrepancy itself simply doesn't surface
 * anymore. The row can be cleaned up lazily.
 */
export type LicenseDiscrepancyFieldName = 'license_number' | 'name';

/**
 * Provenance of a snooze row. Distinguishes a snooze the rep clicked
 * directly on the Discrepancies tab from one that was auto-created
 * when the rep confirmed a match (F39: "I see the registry name
 * differs from ours — I accept that as part of confirming this
 * link"). The behavioral semantics are identical (both flip the
 * row off the alerts surface, both un-flip on value drift), but
 * the source matters for audit + the UI badge.
 */
export type LicenseDiscrepancySnoozeSource = 'manual' | 'match_confirm';

@Index('idx_ldack_license_field', ['fkLicenseId', 'fieldName'], {})
@Entity('client_license_discrepancy_acknowledgement')
export class LicenseDiscrepancyAcknowledgementEntity extends BaseEntity {
	@Column('int', { name: '_fk_license__id', unsigned: true })
	public fkLicenseId!: number;

	@Column('varchar', { name: 'field_name', length: 50 })
	public fieldName!: LicenseDiscrepancyFieldName;

	@Column('text', { name: 'registry_value_snapshot', nullable: true })
	public registryValueSnapshot!: string | null;

	@Column('text', { name: 'stored_value_snapshot', nullable: true })
	public storedValueSnapshot!: string | null;

	@Column('int', { name: 'snoozed_by_user_id', unsigned: true, nullable: true })
	public snoozedByUserId!: number | null;

	@Column('datetime', { name: 'snoozed_at' })
	public snoozedAt!: Date;

	// F39: provenance — defaults to 'manual' so legacy rows keep
	// their semantics. 'match_confirm' is written when the snooze
	// was auto-created as part of a match-confirmation flow (the
	// rep ticked an Acknowledge checkbox before clicking Confirm
	// on a registry suggestion).
	@Column('varchar', {
		name: 'snooze_source',
		length: 20,
		default: () => "'manual'",
	})
	public snoozeSource!: LicenseDiscrepancySnoozeSource;

	@ManyToOne(() => ClientLicenseEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_license__id', referencedColumnName: 'id' }])
	public fkLicense!: ClientLicenseEntity;
}
